/**
 * SuperFlow API client — thin wrapper around fetch.
 *
 * Two flavours of access:
 *   - `?secret=` query param for share-link routes (anyone with the link).
 *   - `Authorization: Bearer <kinde-token>` for `/api/me/*` (logged-in user).
 */
const API_URL = (() => {
    const env = import.meta.env.VITE_API_URL;
    if (env)
        return env.replace(/\/$/, '');
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
        return 'http://localhost:8787';
    }
    return 'https://api.superflow.neutheria.ch';
})();
const BASE = `${API_URL}/api`;
async function request(path, options = {}) {
    const { secret, token, ...rest } = options;
    const url = new URL(path, API_URL);
    if (secret)
        url.searchParams.set('secret', secret);
    const headers = {
        'Content-Type': 'application/json',
        ...rest.headers,
    };
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url.toString(), { ...rest, headers });
    if (!res.ok) {
        const data = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(data.error ?? res.statusText);
    }
    return res.json();
}
// ─── Per-user (Kinde-authenticated) ─────────────────────────────────────────
export const meApi = {
    list: (token) => request(`${BASE}/me/projects`, { token }),
    create: (token, name, description) => request(`${BASE}/me/projects`, {
        method: 'POST', token,
        body: JSON.stringify({ name, description }),
    }),
    remove: (token, projectId) => request(`${BASE}/me/projects/${projectId}`, {
        method: 'DELETE', token,
    }),
    claim: (token, projectId, editSecret) => request(`${BASE}/me/projects/${projectId}/claim`, {
        method: 'POST', token, secret: editSecret,
    }),
    sync: (token, projects) => request(`${BASE}/me/projects/sync`, {
        method: 'POST', token,
        body: JSON.stringify({ projects }),
    }),
};
// ─── Share-link (secret-based) ──────────────────────────────────────────────
export const api = {
    projects: {
        create: (name, description) => request(`${BASE}/projects`, {
            method: 'POST',
            body: JSON.stringify({ name, description }),
        }),
        get: (projectId, secret) => request(`${BASE}/projects/${projectId}`, { secret }),
        update: (projectId, secret, data) => request(`${BASE}/projects/${projectId}`, {
            method: 'PATCH', secret,
            body: JSON.stringify(data),
        }),
        delete: (projectId, secret) => request(`${BASE}/projects/${projectId}`, {
            method: 'DELETE', secret,
        }),
        regenerateSecrets: (projectId, secret) => request(`${BASE}/projects/${projectId}/regenerate-secrets`, { method: 'POST', secret }),
    },
    timelines: {
        create: (projectId, secret, data) => request(`${BASE}/projects/${projectId}/timelines`, {
            method: 'POST', secret,
            body: JSON.stringify(data),
        }),
        update: (tlId, secret, data) => request(`${BASE}/timelines/${tlId}`, {
            method: 'PATCH', secret,
            body: JSON.stringify(data),
        }),
        delete: (tlId, secret) => request(`${BASE}/timelines/${tlId}`, {
            method: 'DELETE', secret,
        }),
    },
    events: {
        create: (tlId, secret, data) => request(`${BASE}/timelines/${tlId}/events`, {
            method: 'POST', secret,
            body: JSON.stringify(data),
        }),
        update: (evId, secret, data) => request(`${BASE}/events/${evId}`, {
            method: 'PATCH', secret,
            body: JSON.stringify(data),
        }),
        delete: (evId, secret) => request(`${BASE}/events/${evId}`, {
            method: 'DELETE', secret,
        }),
    },
    branches: {
        create: (projectId, secret, data) => request(`${BASE}/projects/${projectId}/branches`, {
            method: 'POST', secret,
            body: JSON.stringify(data),
        }),
        update: (brId, secret, data) => request(`${BASE}/branches/${brId}`, {
            method: 'PATCH', secret,
            body: JSON.stringify(data),
        }),
        delete: (brId, secret) => request(`${BASE}/branches/${brId}`, {
            method: 'DELETE', secret,
        }),
    },
    merges: {
        create: (projectId, secret, data) => request(`${BASE}/projects/${projectId}/merges`, {
            method: 'POST', secret,
            body: JSON.stringify(data),
        }),
        update: (mId, secret, data) => request(`${BASE}/merges/${mId}`, {
            method: 'PATCH', secret,
            body: JSON.stringify(data),
        }),
        delete: (mId, secret) => request(`${BASE}/merges/${mId}`, {
            method: 'DELETE', secret,
        }),
    },
    export: {
        json: (projectId, secret) => {
            const url = new URL(`${BASE}/projects/${projectId}/export`, API_URL);
            url.searchParams.set('secret', secret);
            url.searchParams.set('format', 'json');
            window.open(url.toString(), '_blank');
        },
        csv: (projectId, secret) => {
            const url = new URL(`${BASE}/projects/${projectId}/export`, API_URL);
            url.searchParams.set('secret', secret);
            url.searchParams.set('format', 'csv');
            window.open(url.toString(), '_blank');
        },
    },
};
export { API_URL };
